import { GetMapOfSpeakerLayoutResponse } from 'types/services/hearSpace';
import { logHearSpace } from 'config/hearSpace';

export interface RequestLogEntry {
  timestamp: Date;
  request: any;
  response?: GetMapOfSpeakerLayoutResponse;
  error?: string;
  responseTime?: number;
  cached: boolean;
}

export interface HearSpaceMetrics {
  totalTriggers: number;
  apiCalls: number;
  cacheHits: number;
  cacheMisses: number;
  deduplicatedRequests: number;
  failedRequests: number;
  totalResponseTime: number;
  responseCount: number;
  skippedMaxWait: number;
  requestLog: RequestLogEntry[];
}

const MAX_REQUEST_LOG = 100; // prevent unbounded growth

const metrics: HearSpaceMetrics = {
  totalTriggers: 0,
  apiCalls: 0,
  cacheHits: 0,
  cacheMisses: 0,
  deduplicatedRequests: 0,
  failedRequests: 0,
  totalResponseTime: 0,
  responseCount: 0,
  skippedMaxWait: 0,
  requestLog: [],
};

const appendLog = (entry: RequestLogEntry): void => {
  metrics.requestLog.push(entry);
  if (metrics.requestLog.length > MAX_REQUEST_LOG) {
    metrics.requestLog.shift();
  }
};

export const recordTrigger = (): void => { metrics.totalTriggers++; };

export const recordCacheHit = (
  request: any,
  response: GetMapOfSpeakerLayoutResponse,
): void => {
  metrics.cacheHits++;
  appendLog({ timestamp: new Date(), request, response, cached: true, responseTime: 0 });
};

export const recordCacheMiss = (): void => { metrics.cacheMisses++; };

export const recordApiCall = (): void => { metrics.apiCalls++; };

export const recordApiSuccess = (
  request: any,
  response: GetMapOfSpeakerLayoutResponse,
  responseTime: number,
): void => {
  metrics.totalResponseTime += responseTime;
  metrics.responseCount++;
  appendLog({ timestamp: new Date(), request, response, cached: false, responseTime });
};

export const recordApiFailure = (request: any, error: string): void => {
  metrics.failedRequests++;
  appendLog({ timestamp: new Date(), request, error, cached: false });
};

export const recordDeduplicated = (): void => { metrics.deduplicatedRequests++; };

export const recordSkippedMaxWait = (): void => { metrics.skippedMaxWait++; };

/** Returns a shallow copy of the current metrics (safe to read, not mutate). */
export const getHearSpaceMetrics = (): Readonly<HearSpaceMetrics> => ({ ...metrics });

/** No-op stub kept for API compatibility. Detailed logging disabled in prod. */
export const logRequestHistory = (_limit?: number): void => {};

/** Reset all metrics counters and the request log. */
export const resetHearSpaceMetrics = (): void => {
  metrics.totalTriggers = 0;
  metrics.apiCalls = 0;
  metrics.cacheHits = 0;
  metrics.cacheMisses = 0;
  metrics.deduplicatedRequests = 0;
  metrics.failedRequests = 0;
  metrics.totalResponseTime = 0;
  metrics.responseCount = 0;
  metrics.skippedMaxWait = 0;
  metrics.requestLog = [];
  logHearSpace('Metrics reset');
};
